import Vue from 'vue';
import firebase from '@/plugins/firebase';
import { database } from '@/plugins/firebase';
import InstructorTable from '@/classes/InstructorTable';
import moment from 'moment';
import store from "@/store";
import { CURRENT_SEASON_YEAR } from "@/store/seasonYear";
import { getDocumentVersion, checkVersionConflict, formatVersionConflictError } from '@/utils/versionControl';
import { logActivity, extractReservationCells, createCellId, findMatchingCell, cellDataChanged, extractAvailableCells, findMatchingAvailableCell, availableCellDataChanged } from '@/services/ActivityLogService';
const InstructorTableCollection = 'works' + CURRENT_SEASON_YEAR;
export const InstructorTableModule = {
    namespaced: true,
    state: {
        tables: [],
        copyPasteCell: undefined,
        versions: {}
    },
    mutations: {
        store(state, table) {
            const x = state.tables.find(x => x.date.format('YYYY-MM-DD') === table.data.date);
            if (x) {
                if (x.changedAt === table.data.changedAt) {
                    return;
                }
                state.tables.splice(state.tables.indexOf(x), 1, InstructorTable.loadFromObject(table.data, table.id));
                store.dispatch('instructorTable/storeCache', table.date);
                return;
            }
            state.tables.push(InstructorTable.loadFromObject(table.data, table.id));
        },
        update(state, table) {
            state.tables.find((oldTable, index) => {
                if (oldTable.date.format('YYYY-MM-DD') === table.data.date) {
                    Vue.set(state.tables, index, InstructorTable.loadFromObject(table.data, table.id));
                }
            });
        },
        clear(state) {
            state.tables.splice(0, state.tables.length);
        },
        copy(state, cell) {
            state.copyPasteCell = cell;
        },
        clearCopyPaste(state) {
            state.copyPasteCell = undefined;
        },
        /**
         * Set version for a specific date.
         * Feature: 003-bug-fixes (FR-004)
         */
        setVersion(state, { date, version }) {
            Vue.set(state.versions, date, version);
        },
        /**
         * Clear version for a specific date (when table is unloaded).
         * Feature: 003-bug-fixes (FR-004)
         */
        clearVersion(state, date) {
            Vue.delete(state.versions, date);
        }
    },
    actions: {
        //ToDo Opravit typ u data parametru. měl by stačit pouze datum
        async refreshDate(context, data) {
            const dateHours = context.getters['get'](data.date);
            if (dateHours !== undefined) {
                context.commit('update', {
                    id: dateHours.id,
                    data: {
                        ...dateHours.toSaveObject(),
                        kidSchools: context.rootGetters['kidSchool/find'](data.date)
                    },
                });
            }
        },
        async loadDates(context, dates) {
            const promises = [];
            for (const date of dates) {
                if (context.getters['get'](date)) {
                    return;
                }
                const promise = database.collection(InstructorTableCollection)
                    .where('date', '==', date.format('YYYY-MM-DD'))
                    .get();
                promises.push(promise);
                // async run
                promises.push((async () => {
                    const docs = (await promise).docs;
                    if (docs.length !== 0) {
                        const docData = docs[0].data();
                        // Capture version for optimistic locking (FR-004)
                        const version = getDocumentVersion(docData);
                        context.commit('setVersion', { date: date.format('YYYY-MM-DD'), version });
                        context.commit('store', {
                            id: docs[0].id,
                            data: {
                                ...docData,
                                kidSchools: context.rootGetters['kidSchool/find'](docData.date)
                            },
                            date,
                        });
                    }
                    else {
                        // New document starts at version 0 (FR-004)
                        context.commit('setVersion', { date: date.format('YYYY-MM-DD'), version: 0 });
                        context.commit('store', {
                            data: {
                                date: date.format('YYYY-MM-DD'),
                                concluded: false,
                                kidSchools: context.rootGetters['kidSchool/find'](date),
                            },
                            date,
                        });
                    }
                    context.dispatch('storeCache', date);
                })());
            }
            return Promise.all(promises);
        },
        async store(context, table) {
            const saveData = table.toSaveObject();
            await database.collection(InstructorTableCollection).add(saveData);
            // Log activity for each reserved cell (fire-and-forget)
            const dateKey = table.date.format('YYYY-MM-DD');
            const newCells = extractReservationCells(saveData);
            for (const cell of newCells) {
                const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                logActivity('CREATE', 'reservation', cellId, null, cell);
            }
            // Log activity for each available cell (fire-and-forget)
            const newAvailableCells = extractAvailableCells(saveData);
            for (const cell of newAvailableCells) {
                const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                logActivity('CREATE', 'availability', cellId, null, cell);
            }
        },
        async update(context, table) {
            const dateKey = table.date.format('YYYY-MM-DD');
            const expectedVersion = context.state.versions[dateKey] ?? 0;
            const userId = firebase.auth().currentUser?.uid || 'unknown';
            if (table.id === undefined) {
                if (table.rows.length !== 0) {
                    // New document - include version metadata (FR-004)
                    const saveData = {
                        ...table.toSaveObject(),
                        version: 1,
                        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                        updatedBy: userId,
                    };
                    await database.collection(InstructorTableCollection).add(saveData);
                    // Log activity for each reserved cell (fire-and-forget)
                    const newCells = extractReservationCells(saveData);
                    for (const cell of newCells) {
                        const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                        logActivity('CREATE', 'reservation', cellId, null, cell);
                    }
                    // Log activity for each available cell (fire-and-forget)
                    const newAvailableCells = extractAvailableCells(saveData);
                    for (const cell of newAvailableCells) {
                        const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                        logActivity('CREATE', 'availability', cellId, null, cell);
                    }
                    context.commit('setVersion', { date: dateKey, version: 1 });
                }
                else {
                    context.commit('update', { data: {
                            ...table.toSaveObject(),
                            kidSchools: context.rootGetters['kidSchool/find'](table.date)
                        } });
                }
            }
            else if (table.rows.length === 0) {
                // FR-009: Cannot soft-delete concluded work record
                if (table.concluded === true) {
                    throw new Error('Cannot delete concluded work record');
                }
                // Fetch current document to get cells being deleted
                const docRef = database.collection(InstructorTableCollection).doc(table.id);
                const currentDoc = await docRef.get();
                const currentData = currentDoc.exists ? currentDoc.data() : null;
                const oldCells = extractReservationCells(currentData);
                // Log DELETE for each reserved cell (fire-and-forget)
                for (const cell of oldCells) {
                    const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                    logActivity('DELETE', 'reservation', cellId, cell, null);
                }
                // Log DELETE for each available cell (fire-and-forget)
                const oldAvailableCells = extractAvailableCells(currentData);
                for (const cell of oldAvailableCells) {
                    const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                    logActivity('DELETE', 'availability', cellId, cell, null);
                }
                // Soft delete - set deletedAt timestamp
                const deletedAt = firebase.firestore.Timestamp.now();
                await docRef.update({ deletedAt });
                context.commit('clearVersion', dateKey);
            }
            else {
                const docRef = database.collection(InstructorTableCollection).doc(table.id);
                // Version conflict check before saving (FR-004)
                const currentDoc = await docRef.get();
                const conflict = checkVersionConflict(expectedVersion, currentDoc.exists ? currentDoc.data() ?? null : null);
                if (conflict) {
                    throw new Error(formatVersionConflictError(conflict));
                }
                // Include version metadata in save (FR-004)
                const newVersion = expectedVersion + 1;
                const saveData = {
                    ...table.toSaveObject(),
                    version: newVersion,
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                    updatedBy: userId,
                };
                // Cell-level diff detection for activity logging
                const currentData = currentDoc.exists ? currentDoc.data() : null;
                const oldCells = extractReservationCells(currentData);
                const newCells = extractReservationCells(saveData);
                // Log CREATE for new cells, DELETE for removed cells, UPDATE for modified cells
                for (const newCell of newCells) {
                    const oldCell = findMatchingCell(oldCells, { instructorId: newCell.instructorId, hour: newCell.hour });
                    const cellId = createCellId(dateKey, newCell.instructorId, newCell.hour);
                    if (!oldCell) {
                        // New cell
                        logActivity('CREATE', 'reservation', cellId, null, newCell);
                    }
                    else if (cellDataChanged(oldCell, newCell)) {
                        // Modified cell
                        logActivity('UPDATE', 'reservation', cellId, oldCell, newCell);
                    }
                }
                for (const oldCell of oldCells) {
                    const newCell = findMatchingCell(newCells, { instructorId: oldCell.instructorId, hour: oldCell.hour });
                    if (!newCell) {
                        // Deleted cell
                        const cellId = createCellId(dateKey, oldCell.instructorId, oldCell.hour);
                        logActivity('DELETE', 'reservation', cellId, oldCell, null);
                    }
                }
                // Availability cell-level diff detection for activity logging
                const oldAvailableCells = extractAvailableCells(currentData);
                const newAvailableCells = extractAvailableCells(saveData);
                // Log CREATE for new available cells, DELETE for removed, UPDATE for modified
                for (const newCell of newAvailableCells) {
                    const oldCell = findMatchingAvailableCell(oldAvailableCells, { instructorId: newCell.instructorId, hour: newCell.hour });
                    const cellId = createCellId(dateKey, newCell.instructorId, newCell.hour);
                    if (!oldCell) {
                        // New available cell
                        logActivity('CREATE', 'availability', cellId, null, newCell);
                    }
                    else if (availableCellDataChanged(oldCell, newCell)) {
                        // Modified available cell
                        logActivity('UPDATE', 'availability', cellId, oldCell, newCell);
                    }
                }
                for (const oldCell of oldAvailableCells) {
                    const newCell = findMatchingAvailableCell(newAvailableCells, { instructorId: oldCell.instructorId, hour: oldCell.hour });
                    if (!newCell) {
                        // Deleted available cell
                        const cellId = createCellId(dateKey, oldCell.instructorId, oldCell.hour);
                        logActivity('DELETE', 'availability', cellId, oldCell, null);
                    }
                }
                context.commit('update', { id: table.id, data: {
                        ...table.toSaveObject(),
                        kidSchools: context.rootGetters['kidSchool/find'](table.date)
                    } });
                await docRef.update(saveData);
                // Update local version after successful save (FR-004)
                context.commit('setVersion', { date: dateKey, version: newVersion });
            }
        },
        async destroy(context, table) {
            // FR-009: Cannot soft-delete concluded work record
            if (table.concluded === true) {
                throw new Error('Cannot delete concluded work record');
            }
            const dateKey = table.date.format('YYYY-MM-DD');
            const docRef = database.collection(InstructorTableCollection).doc(table.id);
            // Fetch current document to get cells being deleted
            const currentDoc = await docRef.get();
            const currentData = currentDoc.exists ? currentDoc.data() : null;
            const oldCells = extractReservationCells(currentData);
            // Log DELETE for each reserved cell (fire-and-forget)
            for (const cell of oldCells) {
                const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                logActivity('DELETE', 'reservation', cellId, cell, null);
            }
            // Log DELETE for each available cell (fire-and-forget)
            const oldAvailableCells = extractAvailableCells(currentData);
            for (const cell of oldAvailableCells) {
                const cellId = createCellId(dateKey, cell.instructorId, cell.hour);
                logActivity('DELETE', 'availability', cellId, cell, null);
            }
            // Soft delete - set deletedAt timestamp
            const deletedAt = firebase.firestore.Timestamp.now();
            await docRef.update({ deletedAt });
        },
        async listen(context, data) {
            //context.commit('clear');
            //await context.dispatch('loadCache', date);
            const { date, callback } = data;
            const dateKey = date.format('YYYY-MM-DD');
            return database.collection(InstructorTableCollection)
                .where('date', '==', dateKey)
                .onSnapshot(snapshot => {
                for (let change of snapshot.docChanges()) {
                    const docData = change.doc.data();
                    switch (change.type) {
                        case 'added':
                        case 'modified':
                            // Update version from real-time changes (FR-004)
                            const version = getDocumentVersion(docData);
                            context.commit('setVersion', { date: dateKey, version });
                            context.commit('update', { id: change.doc.id, data: {
                                    ...docData,
                                    kidSchools: context.rootGetters['kidSchool/find'](docData.date)
                                } });
                            context.dispatch('storeCache', date);
                            break;
                        case 'removed':
                            context.commit('clearVersion', dateKey);
                            context.commit('update', { data: {
                                    ...docData,
                                    kidSchools: context.rootGetters['kidSchool/find'](docData.date)
                                } });
                            context.dispatch('storeCache', date);
                            break;
                    }
                    if (callback) {
                        callback(change.type);
                    }
                }
            });
        },
        async storeCache(context, date) {
            localStorage.removeItem(InstructorTableCollection);
            const tables = {};
            const cacheFrom = moment().subtract('3', 'weeks');
            const cacheTo = moment().add('4', 'weeks');
            for (const table of context.state.tables) {
                if (table.date.isBetween(cacheFrom, cacheTo, 'days')) {
                    tables[table.id] = table.toSaveObject();
                }
            }
            try {
                localStorage.setItem(InstructorTableCollection + date.format('YYYY-MM-DD'), JSON.stringify(tables));
            }
            catch (e) { }
        },
        async loadCache(context, date) {
            const cache = localStorage.getItem(InstructorTableCollection + date.format('YYYY-MM-DD'));
            if (!cache) {
                return;
            }
            const tables = JSON.parse(cache);
            for (const table of Object.values(tables)) {
                context.commit('store', {
                    id: table.id,
                    data: {
                        ...table,
                        kidSchools: context.rootGetters['kidSchool/find'](moment(table.date)),
                    },
                    date,
                });
            }
        },
        copyCell(context, cell) {
            context.commit('copy', cell);
        },
        clearCell(context, cell) {
            context.commit('clearCopyPaste');
        }
    },
    getters: {
        all(state) {
            return state.tables;
        },
        async fetchAll(state) {
            const docs = await database.collection(InstructorTableCollection).get();
            const ret = [];
            docs.forEach(doc => {
                ret.push({
                    id: doc.id,
                    data: doc.data(),
                });
            });
            return ret;
        },
        get(state) {
            return (date) => state.tables.find(x => x.date.format('YYYY-MM-DD') === date.format('YYYY-MM-DD'));
        },
        getCopyPasteCell(state) {
            return state.copyPasteCell;
        },
        /**
         * Get version for a specific date.
         * Feature: 003-bug-fixes (FR-004)
         */
        getVersion(state) {
            return (date) => state.versions[date] ?? 0;
        }
    },
};
